import { NetworkStatus } from '@apollo/client'
import { QueryNode, StateAliases } from '@helo/cms'
import { isRuntimeOperationConfig, runtimeOperationTypes } from '@helo/cms'

import { WidgetConfig, WidgetMeta } from '../widget-types'

/**
 * Searches the value structure for query references.
 * Identifies REFERENCE operations where the first element is 'query'.
 *
 * @returns query names that the structure depends on.
 */
export const getDependentQueryIds = (value: any): string[] => {
  if (!value) return []

  // Check if this is a runtime operation config with kind: 'REFERENCE'
  if (
    isRuntimeOperationConfig(value) &&
    value.kind === runtimeOperationTypes.REFERENCE.kind
  ) {
    // For REFERENCE operations, extract the query name if the array starts with 'query'
    const referenceValue = value.value
    if (
      Array.isArray(referenceValue) &&
      referenceValue.length >= 2 &&
      referenceValue[0] === 'query' &&
      typeof referenceValue[1] === 'string'
    ) {
      return [referenceValue[1]]
    }
  }
  if (
    isRuntimeOperationConfig(value) &&
    value.kind === runtimeOperationTypes.CONTENT_NODE_REFERENCE.kind
  ) {
    // For REFERENCE operations, extract the query name if the array starts with 'query'
    const referenceValue = value.value
    if (
      Array.isArray(referenceValue) &&
      typeof referenceValue[0] === 'string'
    ) {
      return [referenceValue[0]]
    }
  }

  // Recursive check for arrays
  if (Array.isArray(value)) {
    return value.flatMap(getDependentQueryIds)
  }

  // Parent creates a circular refrence. This skips that.
  if (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    'parent' in value
  ) {
    const { parent, ...rest } = value
    return Object.values(rest).flatMap(getDependentQueryIds)
  }
  // Recursive check for objects (includes view-config `queryId` / `queryIds` props)
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>
    const fromProps: string[] = []
    if (typeof o.queryId === 'string') {
      fromProps.push(o.queryId)
    }
    if (Array.isArray(o.queryIds)) {
      for (const q of o.queryIds) {
        if (typeof q === 'string') fromProps.push(q)
      }
    }
    if (
      o.queryIds &&
      typeof o.queryIds === 'object' &&
      !Array.isArray(o.queryIds)
    ) {
      for (const q of Object.values(o.queryIds)) {
        if (typeof q === 'string') fromProps.push(q)
      }
    }
    return [...Object.values(value).flatMap(getDependentQueryIds), ...fromProps]
  }

  // Primitive values don't contain queries
  return []
}

const isQueryNodeLoading = (queryNode: QueryNode | null | undefined) => {
  if (!queryNode) return true

  const observableResult = queryNode.observableQuery?.getCurrentResult()
  const storeResult = queryNode.apolloState
  const result = observableResult || storeResult

  if (!result) return true

  // Apollo subscribe defers store writes (setTimeout 0). On back/forward navigation
  // the observable can report data before queryContentNode is mapped in the store.
  if (observableResult?.data && !storeResult?.data) {
    return true
  }

  const loadingStates = [
    NetworkStatus.loading,
    NetworkStatus.setVariables,
    NetworkStatus.fetchMore,
    NetworkStatus.refetch,
  ]

  const hasStoreData = !!storeResult?.data

  if (!hasStoreData) {
    return result.loading || loadingStates.includes(result.networkStatus)
  }

  // Keep showing content during background refetches when cached data exists.
  return false
}

export const resolveDependentQueryLoading = (
  dependentQueryIds: string[],
  queries: QueryNode[],
) => {
  if (dependentQueryIds.length > 0 && queries.length === 0) {
    return {
      dependentQueries: [] as QueryNode[],
      errors: [] as Error[],
      queriesPendingRegistration: true,
      someQueriesAreLoading: true,
    }
  }

  const errors = queries
    .map(queryNode => {
      const result =
        queryNode.observableQuery?.getCurrentResult() || queryNode.apolloState
      return result?.error
    })
    .filter((error): error is Error => error != null)

  return {
    dependentQueries: queries,
    errors,
    queriesPendingRegistration: false,
    someQueriesAreLoading: queries.some(isQueryNodeLoading),
  }
}

export const getStaticWidgetProps = (
  props: Record<string, unknown> | undefined,
) => {
  if (!props) return {}

  return Object.fromEntries(
    Object.entries(props).filter(([, value]) => {
      if (isRuntimeOperationConfig(value as any)) return false
      if (Array.isArray(value)) {
        return !value.some(entry => isRuntimeOperationConfig(entry))
      }
      return true
    }),
  )
}

const WIDGET_STATE_METADATA_KEYS = new Set([
  'widgetId',
  'roles',
  'showIf',
  'colorMode',
  'disabled',
])

export const getCanonicalWidgetProps = (
  state: Record<string, unknown>,
  _widgetType?: string,
): Record<string, unknown> => {
  const normalizedState = StateAliases.normalizeStateObject(state)
  const widgetProps =
    (normalizedState.widgetProps as Record<string, unknown> | undefined) ?? {}
  const rootOptions = Object.fromEntries(
    Object.entries(normalizedState).filter(
      ([key]) => key !== 'widgetProps' && !WIDGET_STATE_METADATA_KEYS.has(key),
    ),
  )

  return { ...widgetProps, ...rootOptions }
}

export const extractDependentQueryIds = (
  widgetConfig: WidgetConfig,
  widgetMeta?: WidgetMeta<any, any> | null,
) => {
  const slotKeys = (widgetMeta?.slots as string[]) ?? []
  const configWithState = widgetConfig as any

  // Check widget-specific props and canonical root-level options.
  const props = configWithState.state
    ? getCanonicalWidgetProps(configWithState.state, widgetConfig.type)
    : ((widgetConfig as { props?: Record<string, unknown> }).props ?? {})
  const propsWithoutSlots = removePropsByKey(props, slotKeys)
  const propsQueryIds = getDependentQueryIds(propsWithoutSlots)

  // Check the entire state object for runtime query references.
  const stateQueryIds = configWithState?.state
    ? getDependentQueryIds(configWithState.state)
    : []

  // Combine and deduplicate
  const dependentQueryIds = [...new Set([...stateQueryIds, ...propsQueryIds])]

  // Add fieldSelector query ID for data-driven widgets
  if (widgetMeta?.isDataDriven) {
    const fieldQueryId = widgetConfig.fieldSelector?.queryId
    if (fieldQueryId) {
      dependentQueryIds.push(fieldQueryId)
    }
  }

  return dependentQueryIds
}

export const removePropsByKey = (
  props: Record<string, any> = {},
  keys: string[] = [],
) =>
  Object.fromEntries(
    Object.entries(props).filter(([key]) => !keys.includes(key)),
  )
